import React from 'react';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';

const calculateDuration = (startTime, endTime) => {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  let durationMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
  if (durationMinutes < 0) {
    durationMinutes += 24 * 60;
  }

  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;

  let duration = '';
  if (hours > 0) {
    duration += `${hours}h `;
  }
  if (minutes > 0 || hours === 0) {
    duration += `${minutes}m`;
  }

  return duration.trim();
};

const ScheduleList = ({
  schedules,
  onEditSchedule,
  onDeleteSchedule,
  isLoading
}) => {
  const { t } = useLanguage();

  const weekDays = [
    { value: '0', label: t('scheduleDialog.monday', 'Monday') },
    { value: '1', label: t('scheduleDialog.tuesday', 'Tuesday') },
    { value: '2', label: t('scheduleDialog.wednesday', 'Wednesday') },
    { value: '3', label: t('scheduleDialog.thursday', 'Thursday') },
    { value: '4', label: t('scheduleDialog.friday', 'Friday') },
    { value: '5', label: t('scheduleDialog.saturday', 'Saturday') },
    { value: '6', label: t('scheduleDialog.sunday', 'Sunday') }
  ];

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-neutral-900" />
      </div>
    );
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
        <p className="text-sm text-gray-500">
          {t('scheduleList.noSchedules', 'No schedules created yet. Please click "Add Schedule" button to create schedules.')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <h4 className="text-sm font-semibold text-gray-900">
        {t('scheduleList.establishedSchedules', 'Established Schedules')}
      </h4>
      {schedules.map((schedule) => (
        <div
          key={schedule.id}
          className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow duration-200 hover:shadow-md"
        >
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h6 className="text-sm font-semibold text-gray-900">
                  {t('scheduleList.scheduleId', 'Schedule ID')} {String(schedule.id).substring(0, 8)}
                </h6>
                {schedule.is_default_schedule && (
                  <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100">
                    {t('scheduleList.default', 'Default')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {schedule.is_default_schedule
                  ? t('scheduleList.defaultScheduleNoDateRange', 'Default schedule (no date restrictions)')
                  : `${t('scheduleList.validPeriod', 'Valid period')}: ${dayjs(schedule.from_date).format('YYYY/MM/DD')} ${t('scheduleList.to', 'to')} ${dayjs(schedule.to_date).format('YYYY/MM/DD')}`}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => onEditSchedule(schedule)}
                className="rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                {t('common.edit', 'Edit')}
              </button>
              <button
                type="button"
                onClick={() => onDeleteSchedule(schedule)}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
              >
                {t('common.delete', 'Delete')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto pb-1">
            <div className="grid min-w-[720px] grid-cols-7 gap-2">
              {weekDays.map((day) => {
                const slots = schedule.schedule?.[day.value] || [];
                const hasSlots = slots.length > 0;
                return (
                  <div
                    key={day.value}
                    className={`min-h-[88px] rounded-lg border p-2.5 ${
                      hasSlots
                        ? 'border-blue-100 bg-blue-50/70'
                        : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className={`mb-2 text-xs font-semibold ${hasSlots ? 'text-blue-900' : 'text-gray-600'}`}>
                      {day.label}
                    </div>
                    {hasSlots ? (
                      <ul className="space-y-1.5">
                        {slots.map((slot, idx) => (
                          <li key={idx} className="rounded-md bg-white/80 px-1.5 py-1 ring-1 ring-blue-100/80">
                            <div className="text-[11px] font-medium leading-tight text-gray-900">
                              {slot.from} – {slot.to}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {calculateDuration(slot.from, slot.to)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-[11px] text-gray-400">
                        {t('scheduleList.noSchedule', 'No schedule')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScheduleList;
